import { motion, AnimatePresence, useInView, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Phone, ArrowRight, Star, X, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { MaskedLines, Reveal, FadeIn } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";
import usePageMeta from "@/hooks/usePageMeta";


const PressMarquee = () => (
  <Marquee
    items={[
      "3D Permanent Brows",
      "Laser Tattoo Removal",
      "Fibroblast Tightening",
      "JetPeel Facials",
      "Carbon Laser Facials",
      "CO\u2082 Peels",
      "3D Visia Skin Analysis",
    ]}
  />
);

const STATES = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Florida","Georgia","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New York","North Carolina","Ohio","Oklahoma","Oregon","Pennsylvania","Texas","Utah","Washington","Wisconsin","Wyoming"];

export const BOOKING_URL = "https://beautesolutions.intakeq.com/booking";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col overflow-hidden bg-cream pb-16" data-testid="hero-section">
      <div className="h-28 shrink-0" aria-hidden="true" />
      <div className="bg-espresso py-4 shrink-0" data-testid="hero-state-ticker">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0">
          <p className="shrink-0 px-6 lg:px-10 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-champagne sm:border-r sm:border-linedark">
            Clients have traveled from all over the United States — and beyond
          </p>
          <div className="overflow-hidden">
            <div className="flex w-max animate-marquee">
              {[false, true].map((dup) => (
                <div className="flex shrink-0" key={dup} aria-hidden={dup}>
                  {STATES.map((s) => (
                    <span key={s} className="flex items-center shrink-0">
                      <span className="font-mono text-sm uppercase tracking-[0.15em] text-cream/90 px-5 whitespace-nowrap">{s}</span>
                      <span className="w-1.5 h-1.5 rotate-45 bg-champagne shrink-0" aria-hidden="true" />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#FDFBF7] border-y border-gold/25 shrink-0" data-testid="hero-stat-band">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-wrap items-center justify-center lg:justify-between gap-x-12 gap-y-8">
          {[
            ["One", "expert — Sherry Corvino"],
            ["14,000+", "procedures completed"],
            ["25", "years of experience"],
            ["Natural", "results, always"],
          ].map(([n, l], i) => (
            <div key={l} className="contents">
              {i > 0 && (
                <span className="hidden lg:block w-1.5 h-1.5 rotate-45 bg-gold/50 shrink-0" aria-hidden="true" />
              )}
              <div data-testid="hero-stat" className="text-center min-w-[130px]">
                <p className="font-serif text-3xl sm:text-4xl text-ink">{n}</p>
                <p className="text-xs font-semibold text-taupe mt-2">{l}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center flex-1 w-full pt-14">
        <div className="lg:col-span-7 min-w-0">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-ink">
            <MaskedLines lines={["Refresh Your Look.", "Renew Your Confidence."]} delay={0.25} />
          </h1>
          <FadeIn delay={0.9}>
            <p className="mt-8 text-base sm:text-lg text-taupe leading-relaxed max-w-lg">
              One expert — Sherry Corvino. Over 25 years of experience and 14,000+
              procedures creating natural results that look like you on your very
              best day. Honest guidance, never hype.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="https://shale.ink"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-services"
                className="group inline-flex items-center gap-3 rounded-full bg-gold text-espresso px-10 py-5 text-base font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
              >
                Explore the services
                <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-booking"
                className="inline-flex items-center gap-2 rounded-full border border-gold/60 text-golddark px-10 py-5 text-base font-semibold transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-espresso"
              >
                Book online
              </a>
            </div>
          </FadeIn>
        </div>

        <div className="lg:col-span-5 relative flex justify-center lg:justify-end">
          <motion.div style={{ y }} className="relative w-full max-w-[400px]">
            <div className="img-frame" data-testid="hero-portrait-frame">
              <img
                src="/images/sherry-portrait-hero.jpg"
                alt="Sherry Corvino, CEO of Beauté Solutions, in Peoria AZ"
                data-testid="hero-portrait"
                className="w-full object-cover aspect-[3/4] max-h-[520px]"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-espresso text-cream px-6 py-4 hidden sm:block">
              <p className="font-serif italic text-lg">Sherry Corvino, CEO</p>
              <p className="text-xs font-semibold text-gold mt-1">BSIT, LC, CPCP, CMLT, LSO</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const CAROUSEL = [
  { src: "/images/portfolio/collage-eyeliner-lips-brows.jpg", d: "Permanent eyeliner, lips & 3D brows — portfolio" },
  { src: "/images/portfolio/fullface-makeover.jpg", d: "Full-face makeover — before & after" },
  { src: "/images/portfolio/titan-eyeliner-removal.webp", d: "Titan XL, 3 sessions + 2 laser eyeliner removal sessions — actual result, not retouched" },
  { src: "/images/portfolio/brow-removal-session1.webp", d: "Brow laser tattoo removal — immediately after 1 session" },
  { src: "/images/portfolio/removal-correction-hairstroke.webp", d: "Laser tattoo removal & correction — 3D hairstroke brows" },
  { src: "/images/portfolio/brow-eyeliner-removal-1hour.jpg", d: "Laser brow and eyeliner tattoo removal — 1 session, 1 hour post recovery" },
  { src: "/images/portfolio/eyeliner-removal-healed.jpg", d: "Laser tattoo eyeliner removal — healed after 1 removal session" },
  { src: "/images/portfolio/precision-migration-offshape.jpg", d: "Precision laser tattoo removal — migration off shape" },
  { src: "/images/portfolio/migrated-eyeliner-1session.jpg", d: "Precision laser migrated eyeliner removal — immediately after 1 session" },
  { src: "/images/portfolio/lower-eyeliner-1session.jpg", d: "Laser tattoo removal lower eyeliner — immediately after 1 session" },
  { src: "/images/portfolio/b27-brows.jpg", d: "Permanent 3D hair stroke brows — before & after (1)" },
  { src: "/images/portfolio/b18-brows.jpg", d: "Permanent 3D hair stroke brows — before & after (2)" },
  { src: "/images/portfolio/b23-brows.jpg", d: "Permanent 3D hair stroke brows — before & after (3)" },
  { src: "/images/portfolio/b19-brows.jpg", d: "Permanent 3D hair stroke brows — before & after (4)" },
  { src: "/images/portfolio/b33-brows.jpg", d: "Permanent 3D hair stroke brows — before & after (5)" },
  { src: "/images/portfolio/designer-brows-healed.jpg", d: "3D bladeless permanent designer brows — healed before & after" },
  { src: "/images/portfolio/designer-brows-eyeliner-healed.jpg", d: "3D bladeless permanent designer brows & eyeliner — healed before & after" },
  { src: "/images/portfolio/fb649-hairstroke.jpg", d: "Permanent 3D hair stroke brows — before & after (6)" },
  { src: "/images/portfolio/fb503-hairstroke.jpg", d: "Permanent 3D hair stroke brows — before & after (7)" },
  { src: "/images/portfolio/fb803-hairstroke.jpg", d: "Permanent 3D hair stroke brows — before & after (8)" },
  { src: "/images/portfolio/h1-brows.jpg", d: "Permanent 3D hair stroke brows — before & after (9)" },
  { src: "/images/portfolio/h2-brows.jpg", d: "Permanent 3D hair stroke brows — before & after (10)" },
  { src: "/images/portfolio/h3-brows.jpg", d: "Permanent 3D hair stroke brows — drawn shape & healed result" },
  { src: "/images/portfolio/h4-brows.jpg", d: "Permanent 3D hair stroke brows — before, design drawing & after" },
  { src: "/images/portfolio/h5-brows.jpg", d: "Permanent 3D hair stroke brows — before, drawing & after" },
  { src: "/images/portfolio/thermaderm-eyelids.jpg", d: "ThermaDerm treatment — upper & lower eyelid tightening, 10 days post" },
  { src: "/images/portfolio/sunspot-removal.jpg", d: "Severe keratosis removal — actual result, not retouched" },
  { src: "/images/portfolio/m3-brows.jpg", d: "Permanent 3D hair stroke brows — before & after" },
  { src: "/images/portfolio/full-permanent-lips.jpg", d: "Full permanent lips — scar revision, natural lip color" },
  { src: "/images/portfolio/fullface-thermaderm.jpg", d: "Full-face ThermaDerm — 1 session, post 60 days" },
];

function ResultsCarousel() {
  const [active, setActive] = useState(null);
  const [playing, setPlaying] = useState(true);
  const trackRef = useRef(null);
  const viewRef = useRef(null);
  const hoverRef = useRef(false);
  const inView = useInView(viewRef, { amount: 0.15 });

  useEffect(() => {
    if (!inView || !playing) return;
    const id = setInterval(() => {
      const el = trackRef.current;
      if (!el || hoverRef.current) return;
      const card = el.querySelector("[data-testid=results-carousel-card]");
      const step = card ? card.getBoundingClientRect().width + 24 : 320;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 12) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: step, behavior: "smooth" });
      }
    }, 3200);
    return () => clearInterval(id);
  }, [inView, playing]);

  const nudge = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    setPlaying(false);
    const card = el.querySelector("[data-testid=results-carousel-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : 320;
    if (dir < 0 && el.scrollLeft <= 4) {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    } else if (dir > 0 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 12) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      el.scrollBy({ left: dir * step, behavior: "smooth" });
    }
  };

  return (
    <section className="pt-8 lg:pt-12 pb-12 lg:pb-16 bg-cream overflow-hidden" data-testid="results-carousel-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-end justify-between gap-8 mb-12" ref={viewRef}>
        <Reveal>
          <p className="font-serif italic text-xl sm:text-2xl text-gold mb-4">Portfolio · proven results</p>
          <h2 className="font-serif text-3xl sm:text-5xl text-ink leading-tight">
            Actual work. <span className="italic">Not retouched.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="hidden md:block">
          <div className="flex items-center gap-4">
            <p className="text-sm text-taupe max-w-xs leading-relaxed">
              Before-and-after results from Sherry's own portfolio —
              brows, eyes, lips and laser correction.
            </p>
            <button
              onClick={() => setPlaying(!playing)}
              data-testid="carousel-toggle"
              aria-label={playing ? "Pause the carousel" : "Play the carousel"}
              className="w-12 h-12 rounded-full bg-gold text-espresso flex items-center justify-center shrink-0 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
            >
              {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
          </div>
        </Reveal>
      </div>

      <div className="relative" data-testid="results-carousel">
        <button
          onClick={() => nudge(-1)}
          data-testid="carousel-prev"
          aria-label="Previous photos"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-gold text-espresso shadow-lg flex items-center justify-center transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => nudge(1)}
          data-testid="carousel-next"
          aria-label="Next photos"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-gold text-espresso shadow-lg flex items-center justify-center transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
        >
          <ChevronRight size={20} />
        </button>
        <div
          ref={trackRef}
          onMouseEnter={() => { hoverRef.current = true; }}
          onMouseLeave={() => { hoverRef.current = false; }}
          className="flex gap-6 overflow-x-auto no-scrollbar scroll-smooth px-6"
          data-testid="results-carousel-track"
        >
          {CAROUSEL.map((c, i) => (
            <figure
              key={i}
              data-testid="results-carousel-card"
              className="w-60 sm:w-[19rem] shrink-0 snap-start"
            >
              <button
                type="button"
                onClick={() => setActive(c)}
                data-testid="portfolio-open-btn"
                aria-label={`Open ${c.d}`}
                className="group block w-full text-left"
              >
                <div className="h-64 sm:h-80 overflow-hidden rounded-3xl bg-espresso">
                  <img
                    src={c.src}
                    alt={c.d}
                    loading="lazy"
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
              </button>
              <figcaption className="mt-3 text-xs font-semibold text-ink/70 leading-relaxed">
                {c.d}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] bg-espresso/95 flex items-center justify-center p-6"
            data-testid="portfolio-lightbox"
            onClick={() => setActive(null)}
          >
            <motion.img
              key={active.src}
              src={active.src}
              alt={active.d}
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[86vh] max-w-[92vw] rounded-2xl object-contain"
            />
            <button
              onClick={() => setActive(null)}
              data-testid="lightbox-close"
              aria-label="Close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-gold text-espresso flex items-center justify-center"
            >
              <X size={20} />
            </button>
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-cream/85 text-sm font-semibold text-center max-w-[80vw]">
              {active.d}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

const VIDEOS = [
  { src: "/media/eye-shields.mp4", d: "Inserting the eye shields — laser eyeliner tattoo removal" },
  { src: "/media/brow-reveal.mp4", d: "The brow reveal — 3D hair stroke brows for a client who started with zero brow hair" },
  { yt: "C9G6Sx8V7Uw", d: "Laser removal of permanent makeup eyeliner" },
  { yt: "BvaTyzgtugM", d: "A brow in under five minutes — bladeless 3D permanent makeup brows by Sherry" },
  { yt: "cXZ4LL0_Mhs", d: "3D bladeless brows — the reveal, by expert artist Sherry Hale" },
  { yt: "5jHhMKDH8rg", d: "3D realism brows — another reveal by Sherry" },
  { yt: "ftjTpMVYTdE", d: "Watercolor permanent lips — a demonstration by Sherry" },
];

function VideoCard({ src, yt, title }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const playBtn = (onClick) => (
    <button
      onClick={onClick}
      data-testid="video-reveal-play"
      aria-label={`Play ${title}`}
      className="absolute inset-0 flex items-center justify-center bg-espresso/30"
    >
      <span className="w-14 h-14 rounded-full bg-gold text-espresso flex items-center justify-center">
        <Play size={20} className="ml-0.5" />
      </span>
    </button>
  );
  return (
    <figure className="w-56 sm:w-64 shrink-0 snap-start" data-testid="video-reveal-card">
      <div className="relative h-48 sm:h-56 overflow-hidden rounded-3xl bg-espresso">
        {yt ? (
          playing ? (
            <iframe
              src={`https://www.youtube.com/embed/${yt}?autoplay=1&rel=0`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="video-reveal-player"
              className="w-full h-full"
            />
          ) : (
            <div className="absolute inset-0" data-testid="video-reveal-player">
              <img
                src={`https://i.ytimg.com/vi/${yt}/hqdefault.jpg`}
                alt={title}
                className="w-full h-full object-cover"
              />
              {playBtn(() => setPlaying(true))}
            </div>
          )
        ) : (
          <>
            <video
              ref={ref}
              src={src}
              preload="metadata"
              playsInline
              controls={playing}
              data-testid="video-reveal-player"
              className="w-full h-full object-contain"
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
            />
            {!playing && playBtn(() => { ref.current.play(); setPlaying(true); })}
          </>
        )}
      </div>
      <figcaption className="mt-3 text-xs font-semibold text-ink/70 leading-relaxed">{title}</figcaption>
    </figure>
  );
}

function VideoReveals() {
  const trackRef = useRef(null);
  const nudge = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-testid=video-reveal-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : 340;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };
  return (
    <section className="pt-8 lg:pt-12 pb-20 lg:pb-28 bg-cream2 border-y border-line overflow-hidden" data-testid="video-reveals-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-end justify-between gap-8 mb-12">
        <Reveal>
          <p className="font-serif italic text-xl sm:text-2xl text-gold mb-4">Video reveals</p>
          <h2 className="font-serif text-3xl sm:text-5xl text-ink leading-tight">
            Watch the work <span className="italic">happen.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="hidden md:block">
          <p className="text-sm text-taupe max-w-xs leading-relaxed">
            Real procedures, unedited moments — exactly what a session
            looks like from the chair.
          </p>
        </Reveal>
      </div>
      <div className="relative" data-testid="video-reveals-carousel">
        <button
          onClick={() => nudge(-1)}
          data-testid="video-carousel-prev"
          aria-label="Previous videos"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-gold text-espresso shadow-lg flex items-center justify-center transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => nudge(1)}
          data-testid="video-carousel-next"
          aria-label="Next videos"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-gold text-espresso shadow-lg flex items-center justify-center transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
        >
          <ChevronRight size={20} />
        </button>
        <div ref={trackRef} className="flex gap-6 overflow-x-auto no-scrollbar scroll-smooth px-6">
          {VIDEOS.map((v) => (
            <VideoCard key={v.src || v.yt} src={v.src} yt={v.yt} title={v.d} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const REVIEWS = [
    { id: "myrna-davis", name: "Myrna Davis", meta: "4 reviews · a year ago", text: "I had a botched eyeliner tattoo for 4 years leaving me very insecure — all that went away within an hour, thanks to Sherry!! Extremely grateful for her expertise in this field. I drove 3 hours to see her and would do it again! For those thinking you can find someone more experienced, knowledgeable and cheaper — STOP LOOKING! There's no one better than Sherry. She is worth every penny!!" },
    { id: "lily-rodriguez", name: "Lily Rodriguez M.", meta: "Local Guide · 17 reviews · 2 years ago", text: "Sherry Corvino was absolutely amazing! I found her through a YouTube video. I did my research and chose her to remove the migrated ink from a bad permanent eyeliner procedure. I was so blessed to travel from deep south Texas to Scottsdale AZ and have Sherry work her magic! If you are able to meet with her, don't waste time! It's life changing!" },
    { id: "trinh-dieu", name: "Trinh Dieu", meta: "3 reviews · 2 years ago", text: "Sherry is wonderful, and I knew I was in good hands. I travelled from New Orleans, Louisiana to Phoenix. I am so happy with the results — with tears of joy after my procedure to remove my permanent eyeliner. After a few days, my eyes have healed quickly and look more open and refreshed. Thank YOU Sherry for taking good care of me." },
    { id: "raina-strieck", name: "Raina Strieck", meta: "7 reviews · 2 years ago", text: "Sherry is wonderful! I traveled from Washington to see her for tattoo eyeliner removal, migration. She explained the procedure and what to expect. She made the process very comfortable. She was able to remove the migration and soften the bottom liner. It was definitely worth traveling to see her." },
    { id: "sasa-daily", name: "Sasa Daily", meta: "5 reviews · 3 years ago", text: "Sherry is very professional, cares about your wellbeing and has state of the art equipment. She exhibits finesse when doing her procedures. I recently had permanent tattoo eyeliner removed by her using laser. The outcome is the best! I'm beyond happy that I found her and recommend her without hesitation." },
    { id: "pam-torgrimson", name: "Pam Torgrimson", meta: "3 reviews · 2 years ago", text: "20 years ago I thought it would be a great idea to have permanent eyeliner tattooed. As I aged, I did not like the way my eyeliner looked anymore. I found Sherry's reviews and I knew she was the one I wanted to do the procedure. I was shocked to see she opened a second location in Scottsdale. Where I live!!!" },
    { id: "lisa-briggs", name: "Lisa Briggs", meta: "4 reviews · 3 years ago", text: "Sherry is amazing! Very detailed and a perfectionist. I've gotten my eyebrows done and they look amazing! She also removed my old eyeliner and did fibroblast around my eyes. Sherry knows her stuff and is an amazing woman. So glad I found her!" },
    { id: "may-szeto", name: "May Szeto", meta: "4 reviews · 3 years ago", text: "I am so glad I found Sherry to remove my eyeliner & eyebrow colors. The colors turned blue and I didn't feel confident with them for a long time. I feel great after removing them. I'll go back to Sherry to redo my eyebrows even though I have to fly from California. I also love all the skin products she recommended." },
    { id: "lynn-collette", name: "Lynn Collette", meta: "2 reviews · 3 years ago", text: "I have been a client of Custom Beauty, now Beauty Solutions, for many, many years. Sherry has done several procedures for me including Thermaderm upper and lower face procedure, permanent eyeliner, and three times microneedling brows. Sherry is very talented and professional and I don't hesitate to recommend her 100%!" },
    { id: "amanda-middleton", name: "Amanda Middleton", meta: "4 reviews · 2 years ago", text: "Sherry is amazing! Very knowledgeable about the services she offers. I have not seen myself without tattooed eyeliner for over 20 years. She explained things through the entire process of the removal, so I was comfortable and understood what was happening. I am so excited!" },
    { id: "inge-bertram", name: "Inge Bertram", meta: "3 reviews · 2 years ago", text: "Sherry was just fantastic with my eyeliner removal. No one would touch eyeliner removal and she did an amazing job! She also removed and \u201cresculptured\u201d my eyebrows! Just beautiful!" },
    { id: "laila-pickard", name: "Laila Pickard", meta: "7 reviews · 3 years ago", text: "Sherry is amazing. Had my old permanent eyeliner partially removed. Looks great now. Her skincare line is fantastic. My skin looks so much better. Will be seeing Sherry for all my skincare needs in the future!" },
  ];

  return (
    <section className="pt-12 lg:pt-16 bg-cream2 border-y border-line" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-12 flex flex-wrap items-end justify-between gap-8">
        <Reveal>
          <p className="font-serif italic text-xl sm:text-2xl text-gold mb-4">Google reviews</p>
          <h2 className="font-serif text-3xl sm:text-5xl text-ink leading-tight flex flex-wrap items-center gap-x-4 gap-y-3">
            <span className="flex gap-1 text-gold" aria-label="5 out of 5 stars" data-testid="reviews-stars">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-7 h-7 sm:w-10 sm:h-10" fill="currentColor" strokeWidth={0} />
              ))}
            </span>
            5.0 from 110 Google reviews
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <a
            href="https://share.google/alK7xYjbe4TUkQ9eC"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-google-link"
            className="group inline-flex items-center gap-2.5 rounded-full bg-gold text-espresso px-6 py-3 text-sm font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
          >
            Read the reviews on Google
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>
      <div className="overflow-x-auto pb-4 no-scrollbar" data-testid="reviews-carousel">
        <div className="flex w-max gap-6 px-6 lg:px-10">
          {REVIEWS.map((r) => (
            <div
              key={r.id}
              data-testid={`review-slide-${r.id}`}
              className="flex h-full w-[320px] sm:w-[360px] shrink-0 flex-col rounded-3xl border border-espresso/10 bg-cream p-7 shadow-[0_20px_50px_-30px_rgba(44,34,30,0.35)]"
            >
              <div className="flex items-center justify-between">
                <div className="flex gap-0.5" aria-label="5 out of 5 stars">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} size={16} className="text-gold" fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-taupe">Google review</span>
              </div>
              <p className="mt-5 text-sm text-ink/80 leading-relaxed flex-1">{r.text}</p>
              <div className="mt-6">
                <p className="text-sm font-semibold text-ink">{r.name}</p>
                <p className="text-xs text-taupe mt-0.5">{r.meta}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-10 lg:mt-14 h-1.5 w-full bg-gold" aria-hidden="true" data-testid="reviews-gold-rule" />
    </section>
  );
}

export default function Home() {
  usePageMeta(
    "Beauté Solutions — Permanent Makeup & Laser Esthetics | Peoria, AZ",
    "Permanent brows, eyeliner & lips, laser tattoo removal, fibroblast tightening and JetPeel facials in Peoria, AZ. 25+ years, 14,000+ procedures. Sherry Corvino, CPCP, CMLT.."
  );
  return (
    <div className="grain">
      <Hero />
      <PressMarquee />
      <ResultsCarousel />
      <VideoReveals />
      <Reviews />
      <CTASection />
    </div>
  );
}
